/**
 * Weekly Behaviour dialog — Mode A (day-of-week) + Mode B
 * (week-by-week palette) settings, with the same scope cascade
 * (Workspace per-tab + User defaults) used by Chart Properties
 * and Fib Levels.
 *
 * Live preview model:
 *   - every checkbox / color / date change dispatches immediately
 *   - opening snapshots all per-tab + user state into rollbackRef
 *   - Cancel restores from rollback; Save commits + PUTs User
 *     scope to the server
 *
 * Non-modal, draggable header — chart stays fully interactive
 * underneath while the dialog is open.
 */
import { useEffect, useRef, useState } from "react";
import {
  useActiveTabId,
  useAppDispatch,
  useAppSelector,
} from "@/state/hooks";
import { ColorPicker } from "@/components/ColorPicker/ColorPicker";
import { formatColor, parseColor } from "@/utils/colorWithAlpha";
import {
  MoreActionsMenu,
  type MoreAction,
} from "@/components/MoreActionsMenu/MoreActionsMenu";
import {
  DEFAULT_WEEK_PALETTE,
  DEFAULT_WEEKLY_BEHAVIOUR,
  setTabWeeklyBehaviour,
  setWeeklyBehaviourDialogOpen,
  type WeeklyBehaviourSettings,
} from "@/state/slices/uiSlice";
import {
  saveUserWeeklyBehaviour,
  setUserWeeklyBehaviour,
} from "@/state/slices/userPrefsSlice";
import { DateTimePicker } from "@/components/DateTimePicker/DateTimePicker";
import styles from "./WeeklyBehaviourDialog.module.css";

type Scope = "workspace" | "user";

const SCOPE_LABELS: Record<Scope, string> = {
  workspace: "Workspace",
  user: "User defaults",
};

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// Which mode's controls are SHOWING in the dialog body. Separate
// from `WeeklyBehaviourSettings.mode`, which decides what renders
// on the chart — the user can be editing Day-tab controls while
// the chart renders Week-mode bands.
type EditTab = "day" | "week";

export function WeeklyBehaviourDialog() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.weeklyBehaviourDialogOpen);

  const activeTabId = useActiveTabId();
  const activeSymbol = useAppSelector(
    (s) => s.ui.byTab[activeTabId]?.symbol ?? "",
  );
  const tabOverride = useAppSelector(
    (s) => s.ui.byTab[activeTabId]?.weeklyBehaviour ?? null,
  );
  const userSettings = useAppSelector((s) => s.userPrefs.weeklyBehaviour);
  const userSaving = useAppSelector((s) => s.userPrefs.saving);

  const [scope, setScope] = useState<Scope>("workspace");
  const [editTab, setEditTab] = useState<EditTab>("day");

  // What we're editing in this scope. `undefined` means "no
  // override at this scope → show the inherit banner instead of
  // the controls".
  const editing: WeeklyBehaviourSettings | undefined =
    scope === "workspace"
      ? tabOverride ?? undefined
      : userSettings ?? undefined;

  // What "Customize" seeds from for the current scope. Workspace
  // pulls the User template when one exists; otherwise the built-
  // in DEFAULT. User scope always starts from DEFAULT (it's the
  // root of the template chain).
  const seedFromLabel: string =
    scope === "workspace"
      ? userSettings
        ? "User defaults"
        : "Built-in defaults"
      : "Built-in defaults";

  const seedSettings = (s: Scope): WeeklyBehaviourSettings => {
    if (s === "workspace") return userSettings ?? DEFAULT_WEEKLY_BEHAVIOUR;
    return DEFAULT_WEEKLY_BEHAVIOUR;
  };

  // Rollback snapshot per open. Captures every tab's override
  // and the user-scope settings so Cancel can undo regardless of
  // which scope or tab the user touched mid-edit.
  const rollbackRef = useRef<{
    tabs: Record<string, WeeklyBehaviourSettings | null>;
    user: WeeklyBehaviourSettings | null;
  } | null>(null);
  const allByTab = useAppSelector((s) => s.ui.byTab);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setScope("workspace");
      setEditTab("day");
      const tabSnap: Record<string, WeeklyBehaviourSettings | null> = {};
      for (const [tid, t] of Object.entries(allByTab)) {
        tabSnap[tid] = t.weeklyBehaviour ?? null;
      }
      rollbackRef.current = {
        tabs: tabSnap,
        user: userSettings ? { ...userSettings } : null,
      };
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Drag-to-reposition — same pattern as ChartColorsDialog.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(
    null,
  );
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origTop: number;
    origLeft: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (open) setPos(null);
  }, [open]);
  const onHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    const tgt = e.target as HTMLElement;
    if (tgt.closest("button, input, select")) return;
    e.preventDefault();
    const rect = (
      e.currentTarget.closest(`.${styles.dialog}`) as HTMLElement | null
    )?.getBoundingClientRect();
    const origLeft = rect?.left ?? 0;
    const origTop = rect?.top ?? 0;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft,
      origTop,
    };
    setPos({ top: origTop, left: origLeft });
    setDragging(true);
  };
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const margin = 40;
      setPos({
        top: Math.max(
          0,
          Math.min(
            window.innerHeight - margin,
            d.origTop + (e.clientY - d.startY),
          ),
        ),
        left: Math.max(
          margin - 460,
          Math.min(
            window.innerWidth - margin,
            d.origLeft + (e.clientX - d.startX),
          ),
        ),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  if (!open) return null;

  /** Replace the whole settings at the current scope. */
  const writeSettings = (next: WeeklyBehaviourSettings) => {
    if (scope === "workspace") {
      dispatch(
        setTabWeeklyBehaviour({
          tabId: activeTabId,
          weeklyBehaviour: next,
        }),
      );
    } else {
      dispatch(setUserWeeklyBehaviour(next));
    }
  };

  const patch = (delta: Partial<WeeklyBehaviourSettings>) => {
    if (!editing) return;
    writeSettings({ ...editing, ...delta });
  };

  const patchDay = (
    idx: number,
    delta: Partial<WeeklyBehaviourSettings["dayHighlights"][number]>,
  ) => {
    if (!editing) return;
    const next = editing.dayHighlights.map((d, i) =>
      i === idx ? { ...d, ...delta } : d,
    );
    writeSettings({ ...editing, dayHighlights: next });
  };

  const patchWeekMode = (
    delta: Partial<WeeklyBehaviourSettings["weekMode"]>,
  ) => {
    if (!editing) return;
    writeSettings({
      ...editing,
      weekMode: { ...editing.weekMode, ...delta },
    });
  };

  const patchPaletteSlot = (idx: number, color: string) => {
    if (!editing) return;
    const palette = editing.weekMode.palette.map((c, i) =>
      i === idx ? color : c,
    );
    writeSettings({
      ...editing,
      weekMode: { ...editing.weekMode, palette },
    });
  };

  /** Create the current scope's settings by cloning the seed
   *  template. For Workspace: copies User defaults (or DEFAULT
   *  when none saved). For User: starts from DEFAULT. */
  const startOverride = () => {
    const seed: WeeklyBehaviourSettings = {
      ...seedSettings(scope),
      dayHighlights: seedSettings(scope).dayHighlights.map((d) => ({
        ...d,
      })),
      weekMode: {
        ...seedSettings(scope).weekMode,
        palette: [...seedSettings(scope).weekMode.palette],
      },
    };
    writeSettings(seed);
  };

  const removeOverride = () => {
    if (scope === "workspace") {
      dispatch(
        setTabWeeklyBehaviour({
          tabId: activeTabId,
          weeklyBehaviour: null,
        }),
      );
    } else {
      dispatch(setUserWeeklyBehaviour(null));
    }
  };

  const copyFromInherited = () => {
    if (scope !== "workspace") return;
    const seed: WeeklyBehaviourSettings = {
      ...seedSettings(scope),
      dayHighlights: seedSettings(scope).dayHighlights.map((d) => ({
        ...d,
      })),
      weekMode: {
        ...seedSettings(scope).weekMode,
        palette: [...seedSettings(scope).weekMode.palette],
      },
    };
    writeSettings(seed);
  };

  const resetToDefaults = () => {
    writeSettings({
      ...DEFAULT_WEEKLY_BEHAVIOUR,
      dayHighlights: DEFAULT_WEEKLY_BEHAVIOUR.dayHighlights.map((d) => ({
        ...d,
      })),
      weekMode: {
        ...DEFAULT_WEEKLY_BEHAVIOUR.weekMode,
        palette: [...DEFAULT_WEEKLY_BEHAVIOUR.weekMode.palette],
      },
    });
  };

  const closeAndDiscard = () => {
    const r = rollbackRef.current;
    if (r) {
      for (const [tid, wb] of Object.entries(r.tabs)) {
        dispatch(
          setTabWeeklyBehaviour({ tabId: tid, weeklyBehaviour: wb }),
        );
      }
      dispatch(setUserWeeklyBehaviour(r.user));
    }
    dispatch(setWeeklyBehaviourDialogOpen(false));
  };

  const closeAndKeep = async () => {
    // PUT the current user-scope settings to the server. Workspace
    // scope rides the existing workspace autosave.
    try {
      await dispatch(
        saveUserWeeklyBehaviour({ settings: userSettings }),
      ).unwrap();
    } catch {
      // Network blip — leave local state as-is; next session's
      // fetchUserWeeklyBehaviour will resync.
    }
    dispatch(setWeeklyBehaviourDialogOpen(false));
  };

  // ---- Render ----

  return (
    <div
      className={styles.dialog}
      role="dialog"
      aria-labelledby="weekly-behaviour-title"
      style={
        pos
          ? { top: pos.top, left: pos.left, transform: "none" }
          : undefined
      }
    >
      <header
        className={`${styles.header} ${dragging ? styles.headerDragging : ""}`}
        onMouseDown={onHeaderMouseDown}
      >
        <h2 id="weekly-behaviour-title" className={styles.title}>
          Weekly Behaviour
        </h2>
        <label className={styles.scopeLabel}>
          <span className={styles.scopeLabelText}>Scope</span>
          <select
            className={styles.scopeSelect}
            value={scope}
            onChange={(e) => setScope(e.target.value as Scope)}
          >
            <option value="workspace">
              Workspace{activeSymbol ? ` (${activeSymbol})` : ""}
            </option>
            <option value="user">{SCOPE_LABELS.user}</option>
          </select>
        </label>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={closeAndDiscard}
          aria-label="Close (discard changes)"
        >
          ×
        </button>
      </header>

      <div className={styles.body}>
        {!editing ? (
          <div className={styles.inheritBanner}>
            <p className={styles.inheritText}>
              {scope === "workspace" ? (
                <>
                  <strong>
                    {activeSymbol
                      ? `This chart (${activeSymbol})`
                      : "This chart"}
                  </strong>{" "}
                  has no Weekly Behaviour settings. Click below to
                  start from <strong>{seedFromLabel}</strong>.
                </>
              ) : (
                <>
                  <strong>No User defaults saved yet.</strong> Click
                  below to create a template (applied on Customize
                  to new charts).
                </>
              )}
            </p>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={startOverride}
            >
              Customize{scope === "workspace" ? " this chart" : ""}
            </button>
          </div>
        ) : (
          <>
            {/* Render-mode selector — independent of the edit-tab
                below. Decides what shows on the chart. */}
            <div className={styles.row}>
              <span />
              <span className={styles.rowLabel}>Display</span>
              <select
                className={styles.scopeSelect}
                value={editing.mode}
                onChange={(e) =>
                  patch({
                    mode: e.target.value as WeeklyBehaviourSettings["mode"],
                  })
                }
              >
                <option value="day">Day only</option>
                <option value="week">Week only</option>
                <option value="both">Both</option>
              </select>
            </div>

            {/* Edit tabs — which mode's controls show below. */}
            <div className={styles.tabRow} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={editTab === "day"}
                className={`${styles.tab} ${editTab === "day" ? styles.tabActive : ""}`}
                onClick={() => setEditTab("day")}
              >
                Day
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={editTab === "week"}
                className={`${styles.tab} ${editTab === "week" ? styles.tabActive : ""}`}
                onClick={() => setEditTab("week")}
              >
                Week
              </button>
            </div>

            {/* Mode A: Day rows */}
            {editTab === "day" &&
              editing.dayHighlights.map((d, i) => {
                const { color, opacity } = parseColor(d.color);
                return (
                  <div
                    key={d.dayOfWeek}
                    className={`${styles.row} ${!d.enabled ? styles.rowDisabled : ""}`}
                  >
                    <input
                      type="checkbox"
                      className={styles.check}
                      checked={d.enabled}
                      onChange={(e) =>
                        patchDay(i, { enabled: e.target.checked })
                      }
                      aria-label={`Highlight ${DAY_LABELS[i]}`}
                    />
                    <span className={styles.rowLabel}>{DAY_LABELS[i]}</span>
                    <ColorPicker
                      value={color}
                      opacity={opacity}
                      onChange={(c, o) =>
                        patchDay(i, { color: formatColor(c, o) })
                      }
                      ariaLabel={`Color for ${DAY_LABELS[i]}`}
                    />
                  </div>
                );
              })}

            {/* Mode B: Week-by-week palette */}
            {editTab === "week" && (
              <>
                <div className={styles.row}>
                  <input
                    type="checkbox"
                    className={styles.check}
                    checked={editing.weekMode.enabled}
                    onChange={(e) =>
                      patchWeekMode({ enabled: e.target.checked })
                    }
                    aria-label="Enable week-by-week bands"
                  />
                  <span className={styles.rowLabel}>
                    Enable week-by-week bands
                  </span>
                  <span />
                </div>
                {/* Palette-size selector. 2 = alternating (week N
                    flips between two colours); 6 = a longer
                    distinct sequence before repeating. Switching
                    sizes preserves existing slot colours where
                    they overlap; new slots get the DEFAULT palette
                    hue at that index. */}
                <div className={styles.row}>
                  <span />
                  <span className={styles.rowLabel}>Palette size</span>
                  <select
                    className={styles.scopeSelect}
                    value={editing.weekMode.palette.length === 2 ? 2 : 6}
                    onChange={(e) => {
                      const target = Number(e.target.value) as 2 | 6;
                      const cur = editing.weekMode.palette;
                      let next: string[];
                      if (target <= cur.length) {
                        next = cur.slice(0, target);
                      } else {
                        next = [
                          ...cur,
                          ...DEFAULT_WEEK_PALETTE.slice(cur.length, target),
                        ];
                      }
                      patchWeekMode({ palette: next });
                    }}
                  >
                    <option value={2}>2 colours (alternating)</option>
                    <option value={6}>6 colours</option>
                  </select>
                </div>
                <div>
                  <p className={styles.sectionLabel}>
                    Palette (rotates per week)
                  </p>
                  {editing.weekMode.palette.map((slot, i) => {
                    const { color, opacity } = parseColor(slot);
                    return (
                      <div
                        key={i}
                        className={`${styles.row} ${!editing.weekMode.enabled ? styles.rowDisabled : ""}`}
                      >
                        <span />
                        <span className={styles.rowLabel}>
                          Week {i + 1}
                        </span>
                        <ColorPicker
                          value={color}
                          opacity={opacity}
                          onChange={(c, o) =>
                            patchPaletteSlot(i, formatColor(c, o))
                          }
                          ariaLabel={`Palette slot ${i + 1}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Shared date range — applies to whichever mode is on. */}
            <div>
              <p className={styles.sectionLabel}>
                Date range (optional)
              </p>
              <div className={styles.dateRange}>
                <div className={styles.dateField}>
                  <label className={styles.dateLabel} htmlFor="wb-from">
                    From
                  </label>
                  <DateTimePicker
                    mode="date"
                    className={styles.dateInput}
                    value={editing.fromDate ?? ""}
                    onChange={(v) => patch({ fromDate: v || null })}
                    ariaLabel="From date"
                    placeholder="From"
                    zone="utc"
                  />
                </div>
                <div className={styles.dateField}>
                  <label className={styles.dateLabel} htmlFor="wb-to">
                    To
                  </label>
                  <DateTimePicker
                    mode="date"
                    className={styles.dateInput}
                    value={editing.toDate ?? ""}
                    onChange={(v) => patch({ toDate: v || null })}
                    ariaLabel="To date"
                    placeholder="To"
                    zone="utc"
                  />
                </div>
              </div>
            </div>

          </>
        )}
      </div>

      <footer className={styles.footer}>
        {(() => {
          const items: MoreAction[] = [];
          if (editing) {
            items.push({
              label: "Reset to defaults",
              onClick: resetToDefaults,
              title:
                "Reset this scope to the built-in defaults (does not save; Cancel still reverts)",
            });
          }
          if (editing && scope === "workspace" && userSettings) {
            items.push({
              label: `Copy from ${seedFromLabel}`,
              onClick: copyFromInherited,
              title: `Replace this chart's settings with ${seedFromLabel}'s current value`,
            });
          }
          if (editing) {
            items.push({
              label: "Remove override",
              onClick: removeOverride,
              title:
                "Remove the override and inherit from the next scope down",
            });
          }
          return (
            <MoreActionsMenu items={items} triggerTitle="More actions" />
          );
        })()}
        <span className={styles.footerSpacer} />
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={closeAndDiscard}
          disabled={userSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={closeAndKeep}
          disabled={userSaving}
        >
          {userSaving ? "Saving…" : "Save"}
        </button>
      </footer>
    </div>
  );
}
